import { isAfter, isSameDay, parseISO } from "date-fns";
import * as z from "zod";
import { isValidBrazilCnpj, isValidBrazilCpf } from "../utils/validation";
import { customAttributesDataObjectSchema } from "./generateCustomAttributesSchema";

export const zod = {
  string: () => z.string().trim().min(1, { message: "Required." }),
};
const invalidCharactersRegex = /["*/:<>?|]/;
export const createValidatedStringSchema = () =>
  z
    .string()
    .trim()
    .min(1, { message: "Required." })
    .refine((val) => !invalidCharactersRegex.test(val), {
      message: 'Input contains forbidden characters: "*/:<>?|',
    });

export const createOptionalValidatedStringSchema = () =>
  z
    .string()
    .optional()
    .refine((val) => val === undefined || !invalidCharactersRegex.test(val), {
      message: 'Input contains forbidden characters: "*/:<>?|',
    });

export const createOptionalStringSchema = () =>
  z
    .string()
    .nullable()
    .optional()
    .transform((val) => (val === "" ? undefined : val));

export const updateGaiaObjectStatusSchema = z.object({
  status: zod.string(),
  statusActionOwner: zod.string(),
  statusDetail: zod.string(),
  effort: zod
    .string()
    .refine(
      (val) => {
        if (!val || val === "") return true;
        return !Number.isNaN(Number(val));
      },
      {
        message: "Must be a number",
      },
    )
    .refine(
      (val) => {
        if (!val || val === "") return true;
        return Number(val) >= 0;
      },
      {
        message: "Effort must be 0 or greater",
      },
    )
    .transform((val) => (val && val !== "" ? Number(val) : undefined)),
});

export const VALID_INPUT_PATTERN =
  /^[A-Za-z0-9áàâãéèêíïóôõöúüçÁÀÂÃÉÈÊÍÏÓÔÕÖÚÜÇ .,&/()º™-]+$/;
export const createMarketplaceSellerDataSchema = z.object({
  reportName: createValidatedStringSchema(),
  sellerName: z
    .string()
    .refine((data) => data.length === 0 || VALID_INPUT_PATTERN.test(data), {
      message: "Seller name contains invalid characters",
    }),
  sellerTaxId: z
    .string()
    .refine(
      (data) =>
        data.length === 0 || isValidBrazilCpf(data) || isValidBrazilCnpj(data),
      {
        message: "Seller id must be a CPF or CNPJ",
      },
    ),
  sellerId: z.string().optional(),
});

// Customer lookup form schema
export const encryptedidRegex = /^[A-Z0-9]+$/;
export const decryptedIdRegex = /^[0-9]+$/;

export const isValidId = (
  id: string,
  isDataEncrypted: string,
  searchType: string,
): boolean => {
  if (searchType === "customerInfo") {
    return id.length >= 3 && decryptedIdRegex.test(id);
  }
  if (isDataEncrypted === "Yes")
    return id.length >= 3 && encryptedidRegex.test(id);
  return id.length >= 3 && decryptedIdRegex.test(id);
};

// Helper function to validate seller IDs
export const isValidSellerId = (id: string): boolean => {
  return id.length >= 3 && /^\d+$/.test(id);
};

export const createCustomerLookupDataSchema = z
  .object({
    reportName: createValidatedStringSchema(),
    searchType: createValidatedStringSchema(),
    idInput: z
      .string()
      .transform((value) =>
        value
          .split(",")
          .map((val) => val.trim())
          .filter((val) => val.length > 0)
          .join(","),
      )
      .optional(),
    // addressInfoInputIds: z.string().optional(),
    customerInfoSearchIdOption: z.string().optional(), // when user choose to search by customerInfo, we have two choices: buyerID and buyerExemptionId
    filesUploaded: z
      .array(z.instanceof(File))
      .optional()
      .refine(
        (value) => {
          if (value === undefined || value.length === 0) return true;
          return value[0].size < 10000000;
        },
        { message: "The file size is over the limit" },
      ),
    isDataEncrypted: createValidatedStringSchema(),
  })
  .superRefine((data, ctx) => {
    if (data.idInput) {
      const ids = data.idInput
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      if (
        ids.length > 10 ||
        !ids.every((id) => isValidId(id, data.isDataEncrypted, data.searchType))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["idInput"],
          message:
            "Maximum of 10 IDs allowed. Each encrypted ID should contain uppercase letters and digits only. Each decrypted ID should contain digits only.",
        });
      }
    }
  });

export const createSellerInformationLookupSchema = z
  .object({
    reportName: createValidatedStringSchema(),
    marketplaceId: createValidatedStringSchema(),
    idInput: z
      .string()
      .transform((value) =>
        value
          .split(",")
          .map((val) => val.trim())
          .filter((val) => val.length > 0)
          .join(","),
      )
      .optional(),
    filesUploaded: z
      .array(z.instanceof(File))
      .optional()
      .refine(
        (value) => {
          if (value === undefined || value.length === 0) return true;
          return value[0].size < 10000000;
        },
        { message: "The file size is over the limit" },
      ),
  })
  .superRefine((data, ctx) => {
    if (data.idInput) {
      const ids = data.idInput
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      if (ids.length > 10 || !ids.every((id) => isValidSellerId(id))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["idInput"],
          message:
            "Maximum of 10 seller IDs allowed. Each seller ID should contain at least 3 characters (letters and digits only).",
        });
      }
    }
  });

/*
 * This schema is used for approval due date only, will be reused by both criticalDataUsageComponentSchema and approvalFormDueDateCommentSchema
 */
const dueDateSchema = z
  .string()
  .min(1, "Due date is required")
  .refine(
    (date) => {
      const selectedDateStr = date;
      const todayStr = new Date().toISOString().split("T")[0];

      return selectedDateStr >= todayStr;
    },
    {
      message: "Due date must be today or a future date",
    },
  );

export const criticalDataUsageComponentSchema = z
  .object({
    reportPurpose: createValidatedStringSchema(),
    isUserAgreed: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions",
    }),
    currentUser: z.string(),
    reviewers: z
      .string({ required_error: "Must have at least one reviewer" })
      .array()
      .nonempty({ message: "Must have at least one reviewer" }),
    dueDate: dueDateSchema,
  })
  .refine(
    (data) => {
      if (data.reviewers?.includes(data.currentUser)) {
        return false;
      }
      return true;
    },
    {
      message: "Reviewers must not contain the report owner",
      path: ["reviewers"],
    },
  );

export const createDataBrazilTransactionReportSchema = z
  .object({
    reportName: createValidatedStringSchema(),
    entity: z.string().min(1, { message: "Required." }),
    state: zod.string(),
    startDate: zod.string(),
    endDate: zod.string(),
    sellerTaxId: z
      .string()
      .refine(
        (data) =>
          data.length > 0 &&
          (isValidBrazilCpf(data) || isValidBrazilCnpj(data)),
        {
          message: "Seller id must be a CPF or CNPJ",
        },
      ),
  })
  .superRefine((data, ctx) => {
    if (data.startDate > data.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "End date cannot be before start date",
      });
    }
  });

export const taskBasicSchema = z.object({
  taskId: zod.string().optional(),
  shortId: z.string().optional(),
  taskType: zod.string(),
  parentTaskId: zod.string(),
  teamName: zod.string(),
  taskName: zod.string(),
  owners: zod.string(),
  status: createOptionalStringSchema(),
  actualStartDate: z.number().optional(),
  actualStartDateString: z.string().optional(),
  actualCompletionDate: z.number().optional(),
  actualCompletionDateString: z.string().optional(),
  milestone: z.boolean().optional(),
  description: z
    .string()
    .max(10000, "Description limited to 10,000 characters")
    .optional(),
  deleted: z.boolean(),
  progress: z.string().optional(),
  taskGroupName: z.string().optional(),
  auditName: z.string().optional(),
  taskGroupType: z.string().nullable().optional(),
  inquiryType: createOptionalStringSchema(),
  subInquiryType: createOptionalStringSchema(),
  complexity: createOptionalStringSchema(),
  priority: createOptionalStringSchema(),
  reasonForCancellation: z.string().optional(),
  reopenHistory: z
    .array(
      z.object({
        reason: z.string(),
        reopenedBy: z.string(),
        reopenedAt: z.number(),
      }),
    )
    .optional(),
  effort: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      if (val === undefined || val === null || val === "") return "";
      return String(val);
    })
    .refine(
      (val) => {
        if (!val || val === "") return true;
        return !Number.isNaN(Number(val));
      },
      {
        message: "Must be a number",
      },
    )
    .refine(
      (val) => {
        if (!val || val === "") return true;
        return Number(val) >= 0;
      },
      {
        message: "Effort must be 0 or greater",
      },
    )
    .transform((val) => (val && val !== "" ? Number(val) : undefined)),
  assigneeTeam: createOptionalStringSchema(),
  assigneeGroup: createOptionalStringSchema(),
  /** Display name of assigneeGroup, resolved on read; absent once the group has been removed. */
  assigneeGroupName: createOptionalStringSchema(),
  assigneeOwner: createOptionalStringSchema(),
  taskAssigneeTeamAllocationDate: z.number().optional(),
  taskAssigneeTeamAllocationDateString: z.string().optional(),
  taxFunctionalArea: createOptionalStringSchema(),
  penaltyRiskLevel: createOptionalStringSchema(),
  riskDetails: z
    .string()
    .max(500, "Risk/Priority details limited to 500 characters")
    .nullable()
    .optional()
    .transform((val) => (val === "" ? undefined : val)),
  dataSource: createOptionalStringSchema(),
  agentStatus: createOptionalStringSchema(),
  /** ISO 8601 (UTC) time the agent status was last changed; server-stamped. */
  agentModifiedAt: createOptionalStringSchema(),
  /** Persisted work type ("Manual" | "Agentic-Sampling" | "Agentic-Data"); absent ⇒ Manual. */
  workType: createOptionalStringSchema(),
  taxAuthorityDueDate: z.number().optional(),
  taxAuthorityDueDateString: z.string().optional(),
});

export const taskGroupSchema = taskBasicSchema.extend({
  plannedStartDate: z.number(),
  plannedStartDateString: z.string().optional(),
  dueDate: z.number(),
  dueDateString: z.string().optional(),
});

export const taskSchema = taskBasicSchema.extend({
  plannedStartDate: z.number().optional(),
  plannedStartDateString: z.string().optional(),
  dueDate: z.number().optional(),
  dueDateString: z.string().optional(),
  order: z.number().nullable().optional(),
  enableEscalationMatrix: z.boolean().optional(),
  gaiaObjectId: z.string().optional(),
  gaiaObjectName: z.string().optional(),
});

// Cross-field validation for cross-team assignment fields: penaltyRiskLevel + dueDate + priority required when assigneeTeam is set; riskDetails required for Critical/High penaltyRiskLevel.
const refineCrossTeamRules = (
  data: {
    assigneeTeam?: string | null;
    penaltyRiskLevel?: string | null;
    riskDetails?: string | null;
    dueDateString?: string | null;
    taxAuthorityDueDateString?: string | null;
    priority?: string | null;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.assigneeTeam && !data.penaltyRiskLevel) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["penaltyRiskLevel"],
      message: "Penalty/risk level is required when an assignee team is set",
    });
  }
  if (data.assigneeTeam && !data.dueDateString) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["dueDateString"],
      message: "Due date is required when an assignee team is set",
    });
  }
  if (data.assigneeTeam && !data.taxAuthorityDueDateString) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["taxAuthorityDueDateString"],
      message:
        "Tax authority due date is required when an assignee team is set",
    });
  }
  if (data.assigneeTeam && !data.priority) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["priority"],
      message: "Priority is required when an assignee team is set",
    });
  }
  if (
    data.assigneeTeam &&
    data.assigneeTeam.trim().length > 0 &&
    (data.penaltyRiskLevel === "Critical" ||
      data.penaltyRiskLevel === "High" ||
      data.priority === "High") &&
    (!data.riskDetails || data.riskDetails.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["riskDetails"],
      message:
        "Risk/Priority details are required when penalty/risk is Critical or High, or priority is High",
    });
  }
};

export const createTaskSchema = taskSchema
  .omit({
    taskId: true,
    taskType: true,
    parentTaskId: true,
    teamName: true,
    plannedStartDate: true,
    actualStartDate: true,
    dueDate: true,
    taxAuthorityDueDate: true,
    actualCompletionDate: true,
    deleted: true,
    taskGroupType: true,
  })
  .superRefine(refineCrossTeamRules);

export const createTaskGroupSchema = taskGroupSchema
  .omit({
    deleted: true,
    owners: true,
    teamName: true,
    plannedStartDate: true,
    dueDate: true,
    taxAuthorityDueDate: true,
    actualStartDate: true,
    actualCompletionDate: true,
    parentTaskId: true,
    taskType: true,
    inquiryType: true,
    subInquiryType: true,
    complexity: true,
    priority: true,
  })
  .refine(
    (input) => {
      if (!input.dueDateString || !input.plannedStartDateString) {
        return true;
      }
      const dueDate = parseISO(input.dueDateString);
      const plannedStartDate = parseISO(input.plannedStartDateString);
      return (
        isAfter(dueDate, plannedStartDate) ||
        isSameDay(dueDate, plannedStartDate)
      );
    },
    {
      message: "Due date should be after planned start date",
      path: ["dueDateString"],
    },
  );

export const updateTaskSchema = taskSchema
  .omit({
    deleted: true,
    plannedStartDate: true,
    dueDate: true,
    taxAuthorityDueDate: true,
    actualStartDate: true,
    actualCompletionDate: true,
    taskGroupType: true,
  })
  .superRefine(refineCrossTeamRules);
export type UpdateTaskFormFields = z.infer<typeof updateTaskGroupSchema>;

export const updateTaskGroupSchema = taskGroupSchema
  .omit({
    deleted: true,
    owners: true,
    plannedStartDate: true,
    dueDate: true,
    taxAuthorityDueDate: true,
    actualStartDate: true,
    actualCompletionDate: true,
    parentTaskId: true,
    taskType: true,
    inquiryType: true,
    subInquiryType: true,
    complexity: true,
    priority: true,
  })
  .refine(
    (input) => {
      if (!input.dueDateString || !input.plannedStartDateString) {
        return true;
      }
      const dueDate = parseISO(input.dueDateString);
      const plannedStartDate = parseISO(input.plannedStartDateString);
      return (
        isAfter(dueDate, plannedStartDate) ||
        isSameDay(dueDate, plannedStartDate)
      );
    },
    {
      message: "Due date should be after planned start date",
      path: ["dueDateString"],
    },
  );

export const commentSchema = z.object({
  commentId: zod.string(),
  parentId: zod.string(),
  actorId: zod.string(),
  actorType: zod.string(),
  attributes: z.record(z.string()),
  content: zod.string(),
  version: z.number(),
  creationTime: z.number(),
  application: zod.string(),
  responses: z.array(z.unknown()).optional(),
});

// TODO: remove all these review related things.
export const reviewRequestAndEditSchema = z.object({
  reviewers: z
    .string()
    .array()
    .nonempty({ message: "Must have at least one reviewer" })
    .max(5, { message: "Cannot have more than 5 reviewers" }),
  dueDateString: z.string().nonempty({ message: "Due date is required" }),
  messageForReviewers: z
    .string()
    .nonempty({ message: "Message for reviewers cannot be empty" })
    .max(2000, "Message must be 2000 characters or less")
    .transform((str) => str.replace(/\s+$/g, "")),
});

// TODO: remove these
export const reviewApprovalSchema = z
  .object({
    responseType: z
      .string({
        required_error: "Please select a response type",
      })
      .nonempty("Response type is required"),
    reassignReviewers: z
      .string()
      .array()
      .max(5, { message: "Cannot have more than 5 reviewers" })
      .optional(),
    comments: z.optional(
      z.string().max(2000, "Message must be 2000 characters or less"),
    ),
  })
  .refine(
    (data) => {
      if (
        data.responseType === "Reassigned" &&
        data.reassignReviewers !== undefined &&
        data.reassignReviewers.length > 0
      ) {
        return true;
      }
      if (
        data.responseType !== "Reassigned" &&
        (data.reassignReviewers == null || data.reassignReviewers?.length === 0)
      ) {
        return true;
      }
      return false;
    },
    {
      message: "Reviewer required",
      path: ["reassignReviewers"],
    },
  )
  .refine(
    (data) => {
      if (
        data.responseType === "Rejected" &&
        (data.comments == null || data.comments.trim().length === 0)
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Rejection reason required",
      path: ["comments"],
    },
  );

export const approverActionFormSchema = z
  .object({
    responseType: z
      .string({
        required_error: "Please select a response type",
      })
      .nonempty("Response type is required"),
    reassignReviewer: z.string().optional(),
    comments: z
      .string()
      .min(1, "Comment is required")
      .max(2000, "Message must be 2000 characters or less"),
  })
  .refine(
    (data) => !(data.responseType === "REASSIGN" && !data.reassignReviewer),
    {
      message: "Reviewer required",
      path: ["reassignReviewer"],
    },
  )
  .refine(
    (data) => {
      if (
        data.responseType === "Rejected" &&
        (data.comments == null || data.comments.trim().length === 0)
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Rejection reason required",
      path: ["comments"],
    },
  );

export const ApprovalResumeFormSchema = z.object({
  comment: z
    .string()
    .min(1, "Comment is required")
    .max(2000, "Message must be 2000 characters or less"),
});

export const reviewSchema = z.object({
  reviewId: zod.string(),
  auditId: z.string().optional(),
  taskGroupId: z.string().optional(),
  reviewParentId: zod.string(),
  reviewType: zod.string().optional(),
  teamName: z.string().optional(),
  status: zod.string().optional(),
  requestedBy: z.string().optional(),
  reviewer: z.string().optional(),
  deleted: z.boolean().optional(),
  dueDate: z.number().optional(),
  modifiedAt: z.number().optional(),
  dueDateString: z.string(),
  reviewDate: z.number().optional(),
  auditName: z.string().optional(),
  taskGroupName: z.string().optional(),
  taskName: z.string().optional(),
  comments: z.string().optional(),
  messageForReviewers: z.string().optional(),
});

// Approval schema
const approvalStageSchema = z.object({
  approvers: z.array(z.string()).min(1, "At least one reviewer is required"),
  approvalCondition: z.string().min(1, "Approval condition is required"),
});

export const approvalFormDueDateCommentSchema = z.object({
  dueDate: dueDateSchema,
  comment: z.string().min(1, "Notes for reviewers are required").max(2000),
});

export const approvalFormStageSchema = z
  .object({
    currentUser: z.string(),
    stages: z
      .array(approvalStageSchema)
      .min(1, "At least one approval stage is required")
      .refine(
        (stages) => {
          const allReviewers = stages.flatMap((stage) => stage.approvers);
          return new Set(allReviewers).size === allReviewers.length;
        },
        {
          message: "Reviewers must be unique across all stages",
          path: ["stages"],
        },
      ),
  })
  .refine(
    (data) => {
      const allReviewers = data.stages.flatMap((stage) => stage.approvers);
      if (allReviewers.includes(data.currentUser)) {
        return false;
      }
      return true;
    },
    {
      message: "Reviewers must not contain the requester",
      path: ["stages"],
    },
  );

const fieldDependencyConditionSchema = z.object({
  sourceField: z.string(),
  operator: z.enum([
    "=",
    "!=",
    ":",
    "!:",
    "^",
    "!^",
    ">",
    ">=",
    "<",
    "<=",
    "in",
    "not_in",
  ]),
  value: z.union([z.string(), z.string().array()]),
  result: z.string(),
});

const fieldDependencyRuleSchema = z.object({
  conditions: z.array(fieldDependencyConditionSchema),
  defaultResult: z.string().nullable(),
});

const teamSchema = z.object({
  teamId: z.string(),
  ldapGroupId: z.string(),
  teamDescription: z.string(),
  isCritical: z.boolean(),
  enabledFeatures: z.string().array().optional(),
  teamCustomAttributesSchema: z
    .record(z.string(), customAttributesDataObjectSchema)
    .optional(),
  fieldDependencyRules: z
    .record(z.string(), z.record(z.string(), fieldDependencyRuleSchema))
    .optional(),
  dateFormat: z.string(),
  parentTeamResourceId: z.string().optional(),
  defaultDocumentTypes: z.record(z.string(), z.string()).optional(),
});

export const taxAreaSchema = z.object({
  taxAreaId: z.string(),
  teamName: z.string().optional(),
  title: z
    .string({
      required_error: "Title is required.",
    })
    .min(1, "Title is required")
    .max(255, "Title must be less than 255 characters"),
  category: z
    .string({
      required_error: "Category is required.",
    })
    .min(1, "Category is required"),
  status: z.string(),
  description: z
    .string()
    .max(10000, "Description limited to 10000 characters")
    .optional(),
  createdBy: z.string().optional(),
  createdAt: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedAt: z.string().optional(),
});

export const createTaxAreaSchema = taxAreaSchema
  .omit({
    taxAreaId: true,
    teamName: true,
  })
  .refine(
    (data) => {
      if (
        data.title.length > 0 &&
        data.category.length > 0 &&
        data.status.length > 0
      ) {
        return true;
      }
      return false;
    },
    {
      message: "Fields: title, category, and status are required",
      path: ["createTaxArea"],
    },
  );

export const updateTaxAreaSchema = taxAreaSchema
  .omit({
    taxAreaId: true,
    teamName: true,
  })
  .refine(
    (data) => {
      if (
        data.title.length > 0 &&
        data.category.length > 0 &&
        data.status.length > 0
      ) {
        return true;
      }
      return false;
    },
    {
      message: "Fields: title, category, and status are required",
      path: ["createTaxArea"],
    },
  );

/** Matches an `outcomebreakdown`-prefixed UUID `outcomeBreakdownId`. */
export const OUTCOME_BREAKDOWN_UUID_PATTERN =
  /^outcomebreakdown[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;

export const taxYearBreakdownSchema = z.object({
  outcomeBreakdownId: z.string(),
  parentId: z.string(),
  totalTaxAmount: createValidatedStringSchema(),
  totalPenaltyAmount: createValidatedStringSchema(),
  totalInterestAmount: createValidatedStringSchema(),
  totalOtherAmount: createValidatedStringSchema(),
  type: z.literal("Tax year"),
  category: z.string(),
  superseded: z.boolean().optional(),
});

export const taxAreaBreakdownSchema = z.object({
  outcomeBreakdownId: z.string(),
  parentId: z.string(),
  amountReceived: createValidatedStringSchema(),
  amountInError: createValidatedStringSchema(),
  errorRate: createValidatedStringSchema(),
  amountAssessed: createValidatedStringSchema(),
  type: z.literal("Tax area"),
  category: z.string(),
  superseded: z.boolean().optional(),
});

export const taxYearAreaBreakdownSchema = z.object({
  outcomeBreakdownId: z.string(),
  parentId: z.string(),
  taxYear: z.string().regex(/^\d{1,4}$/, "Tax year must be 1-4 digits"),
  taxAreaId: z.string().min(1, "Tax area is required"),
  totalTaxAmount: createValidatedStringSchema(),
  totalPenaltyAmount: createValidatedStringSchema(),
  totalInterestAmount: createValidatedStringSchema(),
  totalOtherAmount: createValidatedStringSchema(),
  dueDate: z.string().min(1, "Due date is required"),
  additionalInformation: z.string().optional(),
  type: z.literal("Tax year & area"),
  category: z.string(),
  superseded: z.boolean().optional(),
});

// Form-state shape for Pre-tax adjustment breakdown rows. `taxYear` and
// `taxAreaId` are form-only fields; on the wire they are encoded into
// `category` as `${taxYear}${taxAreaId}` (same convention as TAXYEARAREA).
export const preTaxAdjustmentBreakdownSchema = z.object({
  outcomeBreakdownId: z.string(),
  parentId: z.string(),
  taxYear: z.string().regex(/^\d{4}$/, "Tax year must be 4 digits"),
  taxAreaId: z.string().min(1, "Tax area is required"),
  preTaxAmount: createValidatedStringSchema().refine((val) => Number(val) > 0, {
    message: "Pre-tax amount must be greater than zero",
  }),
  postTaxOutcomeBreakdownId: z.string().optional(),
  additionalInformation: z.string().optional(),
  type: z.literal("Pre-tax adjustment"),
  category: z.string(),
});

export const outcomeBreakdownSchema = z.union([
  taxYearBreakdownSchema,
  taxAreaBreakdownSchema,
  taxYearAreaBreakdownSchema,
  preTaxAdjustmentBreakdownSchema,
]);

// `outcomeBreakdownId` is kept as an optional field on the wire so the
// update flow can round-trip the client-minted id and let the backend
// atomic-diff match existing rows by id. When absent the backend mints a
// fresh id for a newly created row.
export const createTaxYearBreakdownSchema = taxYearBreakdownSchema
  .omit({ parentId: true })
  .extend({ outcomeBreakdownId: z.string().optional() });

export const createTaxAreaBreakdownSchema = taxAreaBreakdownSchema
  .omit({ parentId: true })
  .extend({ outcomeBreakdownId: z.string().optional() });

export const createTaxYearAreaBreakdownSchema = taxYearAreaBreakdownSchema
  .omit({
    parentId: true,
    taxYear: true,
    taxAreaId: true,
  })
  .extend({
    outcomeBreakdownId: z.string().optional(),
    dueDate: z.number().optional(),
  });

// Wire/create shape sent on assessment create/update. The client mints the
// `outcomeBreakdownId` with the `outcomebreakdown` prefix so the server can
// round-trip it as the DDB PK. `category` encodes `${taxYear}${taxAreaId}`.
export const createPreTaxAdjustmentBreakdownSchema = z.object({
  outcomeBreakdownId: z
    .string()
    .regex(OUTCOME_BREAKDOWN_UUID_PATTERN, "Must be prefixed UUID"),
  type: z.literal("Pre-tax adjustment").default("Pre-tax adjustment"),
  category: z.string(),
  preTaxAmount: createValidatedStringSchema().refine((val) => Number(val) > 0, {
    message: "Pre-tax amount must be greater than zero",
  }),
  postTaxOutcomeBreakdownId: z.string().optional(),
  additionalInformation: z.string().optional(),
});

export const outcomeBreakdownListSchema = z.array(outcomeBreakdownSchema);

export enum OutcomeBreakdownType {
  TAXYEAR = "Tax year",
  TAXAREA = "Tax area",
  TAXYEARAREA = "Tax year & area",
  PRETAXADJUSTMENT = "Pre-tax adjustment",
}

export const assessmentSchema = z.object({
  assessmentId: z.string(),
  auditId: z.string(),
  type: zod.string(),
  dateReceived: zod.string(),
  totalTaxAmount: z.number({ required_error: "Total tax amount is required" }),
  totalPenaltyAmount: z.number({
    required_error: "Total penalty amount is required",
  }),
  totalInterestAmount: z.number({
    required_error: "Total interest amount is required",
  }),
  totalOtherAmount: z.number({
    required_error: "Total other amount is required",
  }),
  notes: z.string().max(3000, "Notes limited to 3000 characters").optional(),
  createdBy: z.string().optional(),
  createdAt: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedAt: z.string().optional(),
  taxYearBreakdown: z.array(taxYearBreakdownSchema).optional(),
  taxAreaBreakdown: z.array(taxAreaBreakdownSchema).optional(),
  taxYearAreaBreakdown: z.array(taxYearAreaBreakdownSchema).optional(),
  preTaxAdjustmentBreakdown: z
    .array(preTaxAdjustmentBreakdownSchema)
    .optional(),
});
export const createAssessmentSchema = assessmentSchema.omit({
  assessmentId: true,
  auditId: true,
});

export const paymentSchema = z.object({
  paymentId: z.string(),
  auditId: z.string(),
  type: z.string(),
  paymentDate: z.string().refine((val) => val !== "", {
    message: "Required",
  }),
  paymentRefNumber: z
    .string()
    .max(100, "Payment reference number must not exceed 100 characters")
    .optional(),
  trackingRefNumber: z
    .string()
    .max(100, "Tracking reference number must not exceed 100 characters")
    .optional(),
  paymentAuthorizationLink: z
    .string()
    .max(255, "Payment authorization link must not exceed 255 characters")
    .optional(),
  totalTaxAmount: z.number({ required_error: "Total tax amount is required" }),
  totalPenaltyAmount: z.number({
    required_error: "Total penalty amount is required",
  }),
  totalInterestAmount: z.number({
    required_error: "Total interest amount is required",
  }),
  totalOtherAmount: z.number({
    required_error: "Total other amount is required",
  }),
  notes: z.string().max(3000, "Notes limited to 3000 characters").optional(),
  createdBy: z.string().optional(),
  createdAt: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedAt: z.string().optional(),
  taxYearBreakdown: z.array(taxYearBreakdownSchema).optional(),
  taxAreaBreakdown: z.array(taxAreaBreakdownSchema).optional(),
  taxYearAreaBreakdown: z.array(taxYearAreaBreakdownSchema).optional(),
});
export const createPaymentSchema = paymentSchema.omit({
  paymentId: true,
  auditId: true,
});

export const createDocumentSchema = z.object({
  documentType: zod.string(),
  fileName: z
    .string()
    .max(255, "Name limited to 255 characters")
    .refine((val) => !invalidCharactersRegex.test(val), {
      message: 'Input contains forbidden characters: "*/:<>?|',
    })
    .optional(),
});

export const contactFormSchema = z.object({
  fullName: createValidatedStringSchema().refine(
    (val) => /^[^"*/:<>?|]+$/.test(val),
    {
      message: "Invalid full name",
    },
  ),
  jobTitle: z.string().optional(),
  email: z
    .string()
    .refine(
      (val) =>
        val === "" ||
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val),
      { message: "Invalid email address" },
    )
    .optional(),
  phoneNumber: z
    .string()
    .refine(
      (val) => /^[a-zA-Z0-9_()&$?!@%+=^*{|}~':#[\]|/\\,\s–. -]*$/.test(val),
      "Invalid phone number",
    )
    .optional(),
  address: createOptionalValidatedStringSchema(),
  country: z.string().optional(),
  state: z.string().optional(),
  jurisdiction: createOptionalValidatedStringSchema(),
  notes: z.string().max(3000, "Notes limited to 3000 characters").optional(),
  contractAuditor: z.boolean().optional(),
});

export type ContactType = z.infer<typeof contactFormSchema>;

export type CreateAssessmentFormFields = z.infer<typeof createAssessmentSchema>;

export type CreatePaymentFormFields = z.infer<typeof createPaymentSchema>;

export type CreateTaxYearOutcomeBreakdownEntry = z.infer<
  typeof createTaxYearBreakdownSchema
>;
export type CreateTaxAreaOutcomeBreakdownEntry = z.infer<
  typeof createTaxAreaBreakdownSchema
>;
export type CreateTaxYearAreaOutcomeBreakdownEntry = z.infer<
  typeof createTaxYearAreaBreakdownSchema
>;
export type CreatePreTaxAdjustmentOutcomeBreakdownEntry = z.infer<
  typeof createPreTaxAdjustmentBreakdownSchema
>;

export type CreateTaskGroupFormFields = z.infer<typeof createTaskGroupSchema>;
export type UpdateTaskGroupFormFields = z.infer<typeof updateTaskGroupSchema>;

export type CreateTaxAreaFormFields = z.infer<typeof createTaxAreaSchema>;
export type UpdateTaxAreaFormFields = z.infer<typeof updateTaxAreaSchema>;

// Schema derived types
export type Assessment = z.infer<typeof assessmentSchema>;
export type Payment = z.infer<typeof paymentSchema>;
export type OutcomeBreakdown = z.infer<typeof outcomeBreakdownSchema>;
export type TaxYearBreakdown = z.infer<typeof taxYearBreakdownSchema>;
export type TaxAreaBreakdown = z.infer<typeof taxAreaBreakdownSchema>;
export type TaxYearAreaBreakdown = z.infer<typeof taxYearAreaBreakdownSchema>;
export type PreTaxAdjustmentBreakdown = z.infer<
  typeof preTaxAdjustmentBreakdownSchema
>;
export type Task = z.infer<typeof taskSchema> & { createdAt?: number };
export type Comment = z.infer<typeof commentSchema>;
export type Review = z.infer<typeof reviewSchema>;
export type Team = z.infer<typeof teamSchema>;
export type TeamNonCustomAttributes = Omit<Team, "teamCustomAttributesSchema">;
export type FieldDependencyRule = z.infer<typeof fieldDependencyRuleSchema>;
export type FieldDependencyCondition = z.infer<
  typeof fieldDependencyConditionSchema
>;
export type TaxArea = z.infer<typeof taxAreaSchema>;

export type CreateDocumentFields = z.infer<typeof createDocumentSchema>;

export interface DocumentVersion {
  versionId: string;
  s3Key: string;
  size: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
}

export interface Document {
  documentId: string;
  fileName: string;
  extensionType: string;
  parentId: string;
  teamName: string;
  gaiaObjectId?: string;
  s3Key?: string;
  size?: string;
  documentType: string;
  deleted: boolean;
  externalUrl?: string;
  securityLevel: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  isFolder: boolean;
  versions?: DocumentVersion[];
  kbS3Key?: string;
  atlasObjectKey?: string;
  kbIngestionStatus?: string;
  description?: string;
}

/*
 * Assignment groups: named subgroups of a team that a cross-team task can notify instead of the
 * team's whole distribution list. Only the FORM is described here, as it is the only part needing
 * runtime validation - the API record's shape is a type, declared beside the other API types in
 * components/assignment-groups/definitions.ts.
 */

const AMAZON_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@amazon\.com$/;

/**
 * The create/edit form. `entityKeys` holds the entity options' composite keys rather than pairs,
 * because that is what the multiselect binds to; it is split into {entity, entityCode} on submit.
 */
export const assignmentGroupFormSchema = z
  .object({
    /** Drives whether a shared address is collected; not persisted. */
    notifySharedEmail: z.boolean().optional(),
    groupName: z
      .string()
      .trim()
      .min(1, "Group name is required")
      .max(255, "Group name must be less than 255 characters")
      .refine((val) => !invalidCharactersRegex.test(val), {
        message: 'Input contains forbidden characters: "*/:<>?|',
      })
      // Every name with nothing alphanumeric normalizes to the same empty string, so the service refuses
      // it rather than reporting the next one as a duplicate. Diacritics are stripped first, as it folds
      // them too.
      .refine(
        (val) =>
          /[a-zA-Z0-9]/.test(val.normalize("NFD").replace(/\p{M}/gu, "")),
        { message: "Group name must contain at least one letter or number" },
      ),
    type: z.string().min(1, "Type is required"),
    members: z.string().array().min(1, "A group must have at least one member"),
    sharedEmail: z
      .string()
      .trim()
      .refine((val) => val === "" || AMAZON_EMAIL_REGEX.test(val), {
        message: "Enter an @amazon.com address",
      })
      .optional(),
    countries: z.string().array().optional(),
    entityKeys: z.string().array().optional(),
    taxTypes: z.string().array().optional(),
    noticeTypes: z.string().array().optional(),
    litigationTypes: z.string().array().optional(),
  })
  // The checkbox says "instead of members", so a ticked box with no address would notify nobody.
  .refine((data) => !data.notifySharedEmail || !!data.sharedEmail?.trim(), {
    message: "Enter the shared email address to notify",
    path: ["sharedEmail"],
  });

export type AssignmentGroupFormFields = z.infer<
  typeof assignmentGroupFormSchema
>;
